export const memoryStore = () => {
    const m = new Map();
    return { get: (k) => m.get(k) ?? null, set: (k, v) => void m.set(k, v), remove: (k) => void m.delete(k) };
};
export const localStorageStore = () => ({
    get: (k) => (typeof localStorage === "undefined" ? null : localStorage.getItem(k)),
    set: (k, v) => localStorage.setItem(k, v),
    remove: (k) => localStorage.removeItem(k),
});
export class ApiError extends Error {
    status;
    body;
    constructor(status, body) {
        super(`API ${status}`);
        this.status = status;
        this.body = body;
    }
}
export class EctApiClient {
    baseUrl;
    store;
    fetchImpl;
    refreshing = null;
    listeners = new Set();
    constructor(baseUrl, store = localStorageStore(), fetchImpl = fetch.bind(globalThis)) {
        this.baseUrl = baseUrl;
        this.store = store;
        this.fetchImpl = fetchImpl;
    }
    /** Stable per-installation id (PRD M01-04 device registry, M14-04 idempotency). */
    get deviceId() {
        const existing = this.store.get("ect.deviceId");
        if (typeof existing === "string" && existing)
            return existing;
        const id = `dev-${crypto.randomUUID()}`;
        void this.store.set("ect.deviceId", id);
        return id;
    }
    async session() {
        const raw = await this.store.get("ect.session");
        return raw ? JSON.parse(raw) : null;
    }
    async setSession(s) {
        if (s)
            await this.store.set("ect.session", JSON.stringify(s));
        else
            await this.store.remove("ect.session");
        this.listeners.forEach((l) => l(s));
    }
    onSession(l) {
        this.listeners.add(l);
        return () => this.listeners.delete(l);
    }
    async login(identifier, password, opts = {}) {
        const res = await this.fetchImpl(`${this.baseUrl}/api/auth/login`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ identifier, password, deviceId: this.deviceId, ...opts }) });
        if (!res.ok)
            throw new ApiError(res.status, await res.json().catch(() => null));
        const json = (await res.json());
        await this.setSession(json);
        return json;
    }
    async logout() {
        const s = await this.session();
        if (s)
            await this.fetchImpl(`${this.baseUrl}/api/auth/logout`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ refreshToken: s.refreshToken }) }).catch(() => undefined);
        await this.setSession(null);
    }
    async refresh() {
        if (this.refreshing)
            return this.refreshing;
        this.refreshing = (async () => {
            const s = await this.session();
            if (!s)
                return false;
            const res = await this.fetchImpl(`${this.baseUrl}/api/auth/refresh`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ refreshToken: s.refreshToken }) });
            if (!res.ok) {
                if (res.status === 401)
                    await this.setSession(null); // rotated/revoked → signed out
                return false;
            }
            await this.setSession((await res.json()));
            return true;
        })().finally(() => (this.refreshing = null));
        return this.refreshing;
    }
    /** Authenticated request; on 401 tries one refresh then replays the request. */
    async fetch(path, init = {}) {
        const doFetch = async () => {
            const s = await this.session();
            const headers = new Headers(init.headers);
            if (s)
                headers.set("authorization", `Bearer ${s.accessToken}`);
            return this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers });
        };
        let res = await doFetch();
        if (res.status === 401 && (await this.refresh()))
            res = await doFetch();
        return res;
    }
    async json(path, init) {
        const res = await this.fetch(path, init);
        if (!res.ok)
            throw new ApiError(res.status, await res.json().catch(() => null));
        return res.status === 204 ? undefined : (await res.json());
    }
}
//# sourceMappingURL=api-client.js.map